import React from 'react';

import { MCButton, TextField, SelectField, View } from 'src/components/basic';
import { BASE_ICONS } from 'src/components/svg';
import BaseComponent from 'src/components/BaseComponent';
import AlertManager from 'src/components/alert';
import ShortInfo from 'src/components/user/ShortInfo';
import constants from 'src/constants';

import { lang, remCss } from 'src/helpers';

import model from './model';
import NavigationButtons from './NavigationButtons';
import TOOLTIP_CONST from 'src/helpers/lang/tooltip';
import LANG_CONST from 'src/helpers/lang/const';
import Tooltip from 'src/components/basic/Tooltip';

const followUpDurationOptions = [
  { value: 15, label: `15 ${lang.t('Days')}` },
  { value: 30, label: `30 ${lang.t('Days')}` },
  { value: 45, label: `45 ${lang.t('Days')}` },
  { value: 60, label: `60 ${lang.t('Days')}` },
  { value: 90, label: `90 ${lang.t('Days')}` },
  { value: 180, label: `6 ${lang.t('Months')}` },
  { value: 270, label: `9 ${lang.t('Months')}` },
  { value: 365, label: `12 ${lang.t('Months')}` }
];

export default class ConsultationCharge extends BaseComponent {
  componentDidMount() {
    model.subscribe(this.subscription);
    if (model.state.user) model.onLoad();
  }

  componentWillUnmount() {
    model.unsubscribe(this.subscription);
  }

  getCurrency = () => {
    return model.input('currency');
  };

  saveCharges = (e) => {
    e.preventDefault();
    model.saveCharges(() => {
      const { invalid } = model.getApiState('charges');

      const errorAll = Object.values(invalid).find((e) => !!e['required_all']);

      if (errorAll && errorAll['required_all']) {
        AlertManager.popup(AlertManager.TYPES.WARN, {
          close: false,
          title: errorAll['required_all'],
          actionButton: {
            label: LANG_CONST.buttons.ok,
            action: ({ hide }) => hide()
          }
        });
      }
    });
  };
  removeCharge = (item) => {
    AlertManager.popup(AlertManager.TYPES.WARN, {
      close: false,
      title: LANG_CONST.notice.delete,
      cancelButton: {
        label: LANG_CONST.buttons.no,
        action: (actions) => actions.hide()
      },
      actionButton: {
        label: LANG_CONST.buttons.yes,
        action: (actions) => {
          actions.setLoading(true);
          model.removeCharge(item, () => {
            actions.hide();
            actions.setLoading(false);
          });
        }
      }
    });
  };

  renderPrices = (dataSet, name, title, desc1, desc2, tooltip) => {
    const id = dataSet.id || 'default';
    const data = dataSet[name] || {};
    const { handleCharges } = model;
    const { invalid, loading } = model.getApiState('charges');

    return (
      <div className={'mc-flex-20-col'} key={'form-group-' + id + '-' + name}>
        <div className={'mc-flex-20'}>
          <div className={'mc-value-518'}>{lang.t(title)}</div>
          {tooltip && (
            <Tooltip markIcon={'?'} text={tooltip} layerProp={{ placement: 'right-center' }} />
          )}
        </div>
        <div className={'mc-flex-20'}>
          {[
            {
              n: 'virtual',
              l: LANG_CONST.virtual,
              d: desc1,
              tooltip:
                id === 'default' && name === 'first'
                  ? model.state.user?.isDoctor
                    ? TOOLTIP_CONST.doctor_virtual_consultation
                    : TOOLTIP_CONST.clinic_virtual_consultation
                  : null
            },
            { n: 'office', l: LANG_CONST.office, d: desc2 }
          ].map((option) => {
            const handelChange = handleCharges(id, name, option.n);

            const key = [id, name, option.n].join('_');
            return (
              <div className={'mc-grow'} key={key}>
                <TextField
                  template={3}
                  type={'money'}
                  currency={this.getCurrency()}
                  placeholder={LANG_CONST.not_set}
                  labelHtml={
                    <View style={{ flexDirection: 'row', gap: remCss(20), alignItems: 'center' }}>
                      {lang.t(option.l)}{' '}
                      {option.tooltip && (
                        <Tooltip
                          markIcon={'?'}
                          text={option.tooltip}
                          layerProp={{ placement: 'right-center' }}
                        />
                      )}
                    </View>
                  }
                  name={option.n}
                  value={data[option.n]}
                  onChange={handelChange}
                  invalid={invalid[key]}
                  disabled={loading}
                />
                <div className={'mc-form-description'}>{option.d}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  renderSet = (dataSet) => {
    const id = dataSet.id || 'default';

    const auth = model.state.user;

    const user = auth?.isClinic && (model.state.doctors || []).find((d) => d.id == id);
    const hospital =
      auth?.isDoctor && !auth?.createdBy && (model.state.hospitals || []).find((d) => d.id == id);
    let followUpDuration = model.input('followupDuration') || 30;

    return (
      <div className={'mc-flex-20-col'} key={id}>
        {user && (
          <div className={'mc-flex-20'}>
            <ShortInfo user={user} />
            <MCButton
              label={LANG_CONST.buttons.delete}
              iconname={BASE_ICONS.DELETE}
              secondary
              light
              radius
              onClick={() => this.removeCharge(dataSet)}
            />
          </div>
        )}
        {hospital && (
          <div className={'mc-flex-20'}>
            <div className={'mc-flex-5-col mc-grow'}>
              <div className={'mc-label-418'}>{lang.t(LANG_CONST.hospital_name)}</div>
              <div className={'mc-value-520'}>{hospital.hospital?.split('_')[0]}</div>
            </div>
            <MCButton
              label={LANG_CONST.buttons.delete}
              iconname={BASE_ICONS.DELETE}
              secondary
              light
              radius
              onClick={() => this.removeCharge(dataSet)}
            />
          </div>
        )}
        {this.renderPrices(
          dataSet,
          'first',
          LANG_CONST.first_consultation,
          LANG_CONST.text.free_first_consultation_online,
          LANG_CONST.text.free_first_consultation_in_person,
          id === 'default' &&
            (model.state.user?.isDoctor
              ? TOOLTIP_CONST.doctor_first_consultation
              : TOOLTIP_CONST.clinic_first_consultation)
        )}
        {this.renderPrices(
          dataSet,
          'followup',
          lang.t(LANG_CONST.text.follow_up, { attr: followUpDuration }),
          LANG_CONST.text.free_follow_up_online,
          LANG_CONST.text.free_follow_up_in_person
        )}
      </div>
    );
  };

  render() {
    const { charge } = model.state;
    let { error, errorMessage, loading, invalid } = model.getApiState('charges');
    if (invalid && invalid.charge) {
      errorMessage = error = invalid.charge.required_all;
    }

    charge.id = 'default';

    return (
      <>
        <div
          className='mc-modal-body'
          style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}
        >
          <div className='mc-form'>
            <div className={'mc-flex-15-col'} style={{ marginBottom: 20 }}>
              <div className={'mc-value-518'}>{lang.t(LANG_CONST.currency)}</div>
            </div>
            <div className={'mc-flex-15-col'} style={{ marginBottom: 20 }}>
              <SelectField
                // style={{ zIndex: 2 }}
                template={'6'}
                isClearable={false}
                minWidth={remCss(150)}
                options={constants.general.currencyOptions}
                name={'currency'}
                // placeholder={'Specialist'}
                value={model.input('currency') || constants.general.defaultCurrency}
                onChange={(n, v) => model.handleChangeCurrency(v)}
              />
            </div>
            <div className={'mc-flex-15-col'} style={{ marginBottom: 20 }}>
              <div className={'mc-value-518'}>{lang.t(LANG_CONST.text.follow_up_period)}</div>
            </div>
            <div className={'mc-flex-15-col'} style={{ marginBottom: 20 }}>
              <SelectField
                // style={{ zIndex: 2 }}
                template={'6'}
                isClearable={false}
                minWidth={remCss(150)}
                options={followUpDurationOptions}
                name={'followUpDuration'}
                description={lang.t(LANG_CONST.text.follow_up_duration_des)}
                // placeholder={'Specialist'}
                value={model.input('followupDuration') || 30}
                onChange={(n, v) => model.handleChangeDuration(v)}
              />
            </div>
            <div className={'mc-flex-15-col'}>{this.renderSet(charge)}</div>
            {!!error && (
              <p className='mc-error' style={{ padding: 20, fontSize: 18 }}>
                {errorMessage}
              </p>
            )}
          </div>
        </div>
        <NavigationButtons
          prev={this.props.prev}
          hide={this.props.close}
          next={() => model.saveCharges((err) => !err && this.props.next())}
          loading={loading}
          labelNext={this.props.labelNext}
        />
      </>
    );
  }
}
